import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, Gift, Percent, MessageSquare, Award, Users } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import HeroTimeline from '../components/landing/HeroTimeline';

const ARCH_IMAGE =
  'https://customer-assets.emergentagent.com/job_dc9e3538-f079-41ff-9232-076517c20916/artifacts/35ae82kc_image.png';
const COLLAGE_IMG =
  'https://customer-assets.emergentagent.com/job_dc9e3538-f079-41ff-9232-076517c20916/artifacts/ymgrqle4_image.png';
const DASHBOARD_IMG =
  'https://customer-assets.emergentagent.com/job_dc9e3538-f079-41ff-9232-076517c20916/artifacts/bj6wh5m5_image.png';
const ARCHEN_LOGO =
  'https://customer-assets.emergentagent.com/job_archen-studio/artifacts/kz0rmfk8_logo.svg';

const benefits = [
  { icon: Gift, title: '3 Months Free', body: 'Full access. No credit card. Enough time to run a real project and feel the difference.' },
  { icon: Percent, title: '50% Off for 6 Months', body: 'After founding pricing locked at signup. No bait-and-switch, ever.' },
  { icon: MessageSquare, title: 'Direct Line to the Founders', body: 'Your feedback gets built, not logged, not triaged. Built.' },
  { icon: Award, title: 'Named Founding Designer', body: 'Your name on archen.io. Part of the story from day one.' },
];

const SectionNumber = ({ n }) => (
  <p className="text-[#A8946F] text-[13px] tracking-[0.2em] mb-10">{n}</p>
);

const FoundingDesigner = () => {
  const [form, setForm] = useState({ name: '', studioName: '', email: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  // Head metadata for this route (SPA). Restores previous values on unmount.
  useEffect(() => {
    const prevTitle = document.title;
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute('content') : null;
    document.title = 'Founding Designer Program | Archen™ Project Memory';
    if (descEl) {
      descEl.setAttribute(
        'content',
        "Join Archen™'s first 50 interior design studios. Lock in founding pricing and help shape the product from day one. Spots are limited.",
      );
    }
    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc !== null) descEl.setAttribute('content', prevDesc);
    };
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const scrollToForm = () => {
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    const parts = form.name.trim().split(/\s+/);
    const first_name = parts[0] || form.name;
    const last_name = parts.slice(1).join(' ');
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          first_name,
          last_name,
          studio_name: form.studioName,
        }),
      });
      if (!resp.ok) throw new Error('Request failed');
      setStatus('success');
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <div className="bg-[#FAF3ED] min-h-screen" data-testid="founding-designer-page">
      <Header />

      {/* ---------- HERO ---------- */}
      <section className="relative min-h-screen overflow-hidden bg-[#F5EDE3]">
        <div className="absolute inset-0 z-0">
          <img src={ARCH_IMAGE} alt="Studio interior" className="w-full h-full object-cover object-[72%_center]" />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to right, #F5EDE3 0%, #F5EDE3 22%, rgba(245,237,227,0.86) 40%, rgba(245,237,227,0.55) 66%, rgba(245,237,227,0.3) 100%)',
            }}
          />
          <div className="absolute inset-0 bg-[#F5EDE3]/20" />
        </div>

        <div className="relative z-10 max-w-[1240px] mx-auto px-6 md:px-10 min-h-screen flex flex-col pt-28 md:pt-32 pb-10">
          <div className="grid lg:grid-cols-[minmax(0,540px)_minmax(0,1fr)] gap-8 lg:gap-10 items-center flex-1">
            <div className="animate-[heroRise_0.8s_ease-out_both]">
              <p data-testid="fd-hero-eyebrow" className="text-[#7A5A2E] text-[11px] tracking-[0.22em] uppercase mb-6">
                Founding Designer Program
              </p>
              <p data-testid="fd-hero-lead-in" className="text-[#5C4F42] text-[15px] leading-snug mb-3 max-w-[440px]">
                Most firms finish a project and lose everything they learned.
              </p>
              <h1
                data-testid="fd-hero-headline"
                className="text-[#1F1812] leading-[1.05] tracking-[-0.02em]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: 'clamp(2.6rem, 4.8vw, 4rem)', textWrap: 'balance' }}
              >
                Your design projects shouldn't live in your inbox.
              </h1>
              <p
                data-testid="fd-hero-keyword"
                className="mt-5 text-[#7A5A2E] text-[11px] tracking-[0.22em] uppercase"
              >
                Founding Designer Program for Interior Design Studios.
              </p>
              <p className="mt-8 text-[#5C4F42] text-[16px] leading-[1.7] max-w-[460px]">
                The decisions you made last year could save you hours this year. Right now, they are sitting in your inbox.
              </p>
              <div className="mt-10">
                <button
                  type="button"
                  onClick={scrollToForm}
                  data-testid="fd-hero-cta"
                  className="group inline-flex items-center gap-2 bg-[#1F1812] text-white text-[14px] font-medium px-8 py-4 rounded-lg transition-all duration-300 hover:bg-[#0F0D0A] hover:shadow-xl hover:shadow-black/20"
                >
                  Apply to join the Founding 50
                  <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>
              </div>
              <div className="mt-10 flex items-center gap-2.5 text-[#7A6B58] text-[14px]">
                <Users size={16} className="text-[#A8946F]" />
                <span>50 spots. No credit card. Free for 3 months.</span>
              </div>
            </div>

            <HeroTimeline />
          </div>
        </div>

        <style>{`
          @keyframes heroFloat { from { transform: translateY(0); } to { transform: translateY(-8px); } }
          @keyframes heroRise { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
      </section>

      {/* ---------- 01 - PROBLEM ---------- */}
      <section className="py-24 md:py-28 bg-[#F8F4F0]">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <SectionNumber n="01" />
            <h2
              className="text-[#1F1812] leading-[1.1] tracking-[-0.02em]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: 'clamp(1.9rem, 3vw, 2.6rem)' }}
            >
              Six months in, a client says they never approved that fixture change.
            </h2>
            <div className="mt-8 text-[#5C4F42] text-[16px] leading-[1.8] space-y-6 max-w-[480px]">
              <div>
                <p>You know they did. It was in an email, or a text, or a call recap from March.</p>
                <p>Now it is your word against theirs, and the proof is buried somewhere in an inbox with four hundred other threads.</p>
              </div>
              <p>The vendor issue. The preference from month two. The decision that took three rounds to land. All of it, gone the moment someone needs it back.</p>
              <p className="text-[#1F1812] font-semibold">So the next project starts from scratch. Again.</p>
            </div>
          </div>
          <div className="overflow-hidden">
            <img src={COLLAGE_IMG} alt="Scattered project emails, drawings, specs and material samples" data-testid="fd-collage" className="w-[161%] max-w-none ml-[-61%]" />
          </div>
        </div>
      </section>

      {/* ---------- 02 - SOLUTION ---------- */}
      <section className="py-24 md:py-28 bg-[#FBF7F3]">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <SectionNumber n="02" />
            <h2
              className="text-[#1F1812] leading-[1.1] tracking-[-0.02em] mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: 'clamp(1.9rem, 3vw, 2.6rem)' }}
            >
              Archen™ fixes that.
            </h2>
            <div className="text-[#5C4F42] text-[16px] leading-[1.8] space-y-6 max-w-[480px]">
              <p>Connect your email, calls, and storage in one click.</p>
              <p>
                <span className="text-[#1F1812] font-semibold">Archen™ reads your projects and builds a live workspace</span>{' '}
                tasks, decisions, approvals, specs, <span className="text-[#1F1812] font-semibold">organized</span> without
                you touching a thing.
              </p>
              <p>
                The longer you use it, the smarter it gets. Your studio builds institutional memory instead of starting over
                every time.
              </p>
            </div>
            <div className="mt-8 bg-[#FAF4EC] border border-[#2A2018]/[0.06] rounded-2xl px-7 py-6 max-w-[340px] flex items-center gap-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-15px_rgba(42,32,24,0.18)]">
              <span className="w-14 h-14 rounded-full bg-[#F2EADD] flex items-center justify-center flex-shrink-0">
                <span
                  className="w-6 h-6 bg-[#8B6D43]"
                  style={{
                    WebkitMaskImage: `url(${ARCHEN_LOGO})`,
                    maskImage: `url(${ARCHEN_LOGO})`,
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskPosition: 'center',
                    WebkitMaskSize: 'contain',
                    maskSize: 'contain',
                  }}
                />
              </span>
              <p className="text-[#6B5E4F] text-[15px] leading-relaxed">
                Design tools manage tasks.
                <br />
                <span className="text-[#1F1812] font-semibold">Archen™ learns projects.</span>
              </p>
            </div>
          </div>
          <div className="overflow-hidden">
            <img src={DASHBOARD_IMG} alt="Archen™ project workspace with timeline, details and connected tools" data-testid="fd-dashboard" className="w-[185%] max-w-none ml-[-85%]" />
          </div>
        </div>
      </section>

      {/* ---------- 03 - COHORT ---------- */}
      <section className="py-24 md:py-28">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <SectionNumber n="03" />
            <h2
              className="text-[#1F1812] leading-[1.1] tracking-[-0.02em]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: 'clamp(1.9rem, 3vw, 2.6rem)' }}
            >
              We're starting with 50 designers.
            </h2>
            <div className="mt-8 text-[#5C4F42] text-[16px] leading-[1.8] space-y-6 max-w-[480px]">
              <p>No public launch. No ads.</p>
              <p>
                We hand-selected a small cohort of working designers to get early access, shape the product directly, and lock
                in founding pricing, permanently.
              </p>
              <p>If you're here, you found us before the launch. That matters to us.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {benefits.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-[#FAF4EC] border border-[#2A2018]/[0.06] rounded-2xl px-7 py-6 flex items-center gap-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-15px_rgba(42,32,24,0.18)]"
              >
                <span className="w-14 h-14 rounded-full bg-[#F2EADD] flex items-center justify-center flex-shrink-0">
                  <Icon size={26} className="text-[#8B6D43]" strokeWidth={1.5} />
                </span>
                <div>
                  <h3 className="text-[#1F1812] text-[16px] font-semibold tracking-tight mb-2">{title}</h3>
                  <p className="text-[#6B5E4F] text-[13.5px] leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- 04 - APPLY ---------- */}
      <section id="apply" className="py-24 md:py-28">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <SectionNumber n="04" />
            <h2
              className="text-[#1F1812] leading-[1.1] tracking-[-0.02em]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: 'clamp(1.9rem, 3vw, 2.6rem)' }}
            >
              50 spots.
              <br />
              Closed when full. Apply now.
            </h2>
            <p className="mt-6 text-[#5C4F42] text-[16px] leading-relaxed max-w-[420px]">
              When a client says they never signed off, you have the receipt.
            </p>
          </div>

          <div className="w-full max-w-[460px]">
            {status === 'success' ? (
              <div
                data-testid="fd-success"
                className="bg-[#FAF4EC] border border-[#2A2018]/[0.08] rounded-2xl px-8 py-10 text-center"
              >
                <span className="w-14 h-14 rounded-full bg-[#F2EADD] flex items-center justify-center mx-auto mb-6">
                  <Check size={26} className="text-[#8B6D43]" strokeWidth={1.75} />
                </span>
                <h3
                  className="text-[#1F1812] mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: 'clamp(1.5rem, 2.4vw, 2rem)' }}
                >
                  Application received.
                </h3>
                <p className="text-[#5C4F42] text-[15px] leading-relaxed">
                  Thank you, {form.name.trim().split(/\s+/)[0] || 'there'}. We'll review your studio and reach out
                  personally within 48 hours.
                </p>
              </div>
            ) : (
            <form onSubmit={onSubmit} data-testid="fd-form">
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-6">
                  <div>
                    <label className="block text-[#6B5E4F] text-[13px] mb-2">Name</label>
                    <input
                      data-testid="fd-input-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={onChange('name')}
                      placeholder="Your name"
                      className="w-full bg-white border border-[#E2D9CB] text-[#1F1812] placeholder-[#A89888] text-[15px] px-4 py-3.5 rounded-md focus:outline-none focus:border-[#C49A57] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[#6B5E4F] text-[13px] mb-2">Studio name</label>
                    <input
                      data-testid="fd-input-studio"
                      type="text"
                      value={form.studioName}
                      onChange={onChange('studioName')}
                      placeholder="Your studio"
                      className="w-full bg-white border border-[#E2D9CB] text-[#1F1812] placeholder-[#A89888] text-[15px] px-4 py-3.5 rounded-md focus:outline-none focus:border-[#C49A57] transition-colors"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-[#6B5E4F] text-[13px] mb-2">Email</label>
                  <input
                    data-testid="fd-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange('email')}
                    placeholder="you@example.com"
                    className="w-full bg-white border border-[#E2D9CB] text-[#1F1812] placeholder-[#A89888] text-[15px] px-4 py-3.5 rounded-md focus:outline-none focus:border-[#C49A57] transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="fd-submit"
                  disabled={status === 'submitting'}
                  className="mt-6 w-full bg-[#1F1812] text-white font-medium text-[15px] rounded-md px-6 py-4 transition-all duration-300 hover:bg-[#0F0D0A] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === 'submitting' ? 'Submitting...' : 'Claim your spot'}
                </button>
                {status === 'error' && (
                  <p data-testid="fd-error" className="mt-4 text-[#B4342B] text-[13px]">
                    Something went wrong. Please try again in a moment.
                  </p>
                )}
                <p className="mt-5 flex items-center gap-2 text-[#8B7355] text-[13px]">
                  <Check size={14} className="text-[#A8946F]" />
                  No credit card. We'll reach out personally within 48 hours.
                </p>
            </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FoundingDesigner;
